import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { type CategoryNode } from '../../category-tree';

/**
 * 학년 선택 관리 hook
 *
 * 선택된 학교급 노드(parentNode)를 받아서 학년 레벨을 관리하고,
 * 선택된 학년 노드를 다음 hook(useSemesterState)에 전달합니다.
 */
export const useSelectGrade = ({
  initialGradeId,
  parentNode,
}: {
  initialGradeId?: string;
  parentNode?: CategoryNode;
}) => {
  const [selectedGradeNode, setSelectedGradeNode] = useState<
    CategoryNode | undefined
  >(() => parentNode?.subNodes.find(node => node.nodeId === initialGradeId));
  const [error, setError] = useState<string>();
  const { t } = useTranslation(['build', 'common']);

  // 학년 노드들 (parentNode의 children)
  const gradeNodes: CategoryNode[] | undefined = useMemo(() => {
    if (!parentNode) return undefined;
    return parentNode.subNodes;
  }, [parentNode]);

  // 검증
  const validate = useCallback((): boolean => {
    if (gradeNodes && gradeNodes.length > 0 && !selectedGradeNode) {
      setError(t('build:schoolInfo.requiredField'));
      return false;
    }
    setError(undefined);
    return true;
  }, [gradeNodes, selectedGradeNode, t]);

  // 에러 클리어
  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return {
    gradeNodes,
    selectedGradeNode,
    setSelectedGradeNode,
    error,
    validate,
    clearError,
  };
};
